import { DEFAULT_DAILY_TIMELINE_DAYS } from './chartTimeline';
import { getDirectPaymentTimestamp } from './directPaymentSort';

export const toLocalDayKey = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const buildEmptyChartBuckets = (dayCount = DEFAULT_DAILY_TIMELINE_DAYS) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: dayCount }, (_, i) => {
        const d = new Date(today.getTime() - (dayCount - 1 - i) * 86400000);
        const key = toLocalDayKey(d);
        return { date: key, labelDate: key, sent: 0, received: 0 };
    });
};

const addToBucket = (buckets, indexByDate, timestampMs, amount, field) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return;
    const dayKey = toLocalDayKey(timestampMs);
    const idx = indexByDate[dayKey];
    if (idx === undefined) return;
    buckets[idx][field] += numericAmount;
};

const amountsMatch = (a, b) =>
    Math.abs(Number(a) - Number(b)) < 1e-12;

/** Skip fulfilled-request row when the same transfer is already a direct payment. */
const isFulfilledRequestAlreadyInDirectPayments = (request, directPayments) => {
    const payer = String(request.receiver || '').toLowerCase();
    const requester = String(request.requester || '').toLowerCase();
    const amount = Number(request.parsedNumber ?? request.amount);
    if (!Number.isFinite(amount)) return false;

    return directPayments.some((payment) => {
        const sender = String(payment.sender || '').toLowerCase();
        const receiver = String(payment.receiver || '').toLowerCase();
        const paymentAmount = Number(payment.parsedNumber ?? payment.amount);
        return (
            sender === payer &&
            receiver === requester &&
            amountsMatch(paymentAmount, amount)
        );
    });
};

/**
 * Daily sent/received totals from completed direct payments plus fulfilled
 * (accepted) payment requests. Pending requests are excluded.
 */
export function buildDashboardChartDataset({
    walletAddress,
    dayCount = DEFAULT_DAILY_TIMELINE_DAYS,
    directPayments = [],
    outgoingPaymentRequests = [],
    incomingPaymentRequests = [],
    dbRecords = [],
    paymentEventTimes = {},
    requestAcceptedEventTimes = {},
}) {
    const buckets = buildEmptyChartBuckets(dayCount);
    if (!walletAddress) return buckets;

    const wallet = walletAddress.toLowerCase();
    const indexByDate = buckets.reduce((acc, bucket, index) => {
        acc[bucket.date] = index;
        return acc;
    }, {});

    const dbTimeByPaymentId = {};
    (dbRecords || []).forEach((record) => {
        if (record?.paymentId && record?.createdAt) {
            dbTimeByPaymentId[String(record.paymentId)] = new Date(
                record.createdAt,
            ).getTime();
        }
    });

    const resolveDirectTimestamp = (payment) => {
        const id = String(payment.paymentId ?? payment.id ?? '');
        const fromDb = dbTimeByPaymentId[id];
        const enriched = {
            ...payment,
            ...(fromDb ? { createdAtMs: fromDb } : {}),
        };
        return getDirectPaymentTimestamp(
            enriched,
            paymentEventTimes,
            requestAcceptedEventTimes,
        );
    };

    const resolveAcceptedRequestTimestamp = (request) => {
        const id = String(request.requestId ?? request.id ?? '');
        return (
            request.acceptedAtMs ??
            requestAcceptedEventTimes[id] ??
            null
        );
    };

    const normalizedDirect = [];
    const seenDirectIds = new Set();
    directPayments.forEach((payment) => {
        const id = String(payment.paymentId ?? payment.id ?? '');
        if (!id || seenDirectIds.has(id)) return;
        seenDirectIds.add(id);

        const amount = Number(payment.parsedNumber ?? payment.amount);
        if (!Number.isFinite(amount) || amount <= 0) return;

        const timestampMs = resolveDirectTimestamp(payment);
        if (!timestampMs) {
            // Skip — do not bucket unknown-time payments onto today (misleading spike).
            return;
        }

        normalizedDirect.push({ ...payment, parsedNumber: amount, createdAtMs: timestampMs });

        const sender = String(payment.sender || '').toLowerCase();
        const receiver = String(payment.receiver || '').toLowerCase();

        if (sender === wallet) {
            addToBucket(buckets, indexByDate, timestampMs, amount, 'sent');
        }
        if (receiver === wallet) {
            addToBucket(buckets, indexByDate, timestampMs, amount, 'received');
        }
    });

    // Backfill chart from backend rows when on-chain history is empty/partial.
    (dbRecords || []).forEach((record) => {
        const id = String(record.paymentId ?? '');
        if (!id || seenDirectIds.has(id)) return;
        const amount = Number(record.amount);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const timestampMs = dbTimeByPaymentId[id];
        if (!timestampMs) return;

        seenDirectIds.add(id);
        const sender = String(record.sender || '').toLowerCase();
        const receiver = String(record.receiver || '').toLowerCase();
        if (sender === wallet) {
            addToBucket(buckets, indexByDate, timestampMs, amount, 'sent');
        }
        if (receiver === wallet) {
            addToBucket(buckets, indexByDate, timestampMs, amount, 'received');
        }
    });

    const addFulfilledRequest = (request, role) => {
        if (!request?.isAccepted) return;

        const amount = Number(request.parsedNumber ?? request.amount);
        if (!Number.isFinite(amount) || amount <= 0) return;

        if (isFulfilledRequestAlreadyInDirectPayments(request, normalizedDirect)) {
            return;
        }

        const timestampMs = resolveAcceptedRequestTimestamp(request);
        if (!timestampMs) return;

        if (role === 'outgoing') {
            addToBucket(buckets, indexByDate, timestampMs, amount, 'received');
            return;
        }

        addToBucket(buckets, indexByDate, timestampMs, amount, 'sent');
    };

    outgoingPaymentRequests.forEach((request) =>
        addFulfilledRequest(request, 'outgoing'),
    );
    incomingPaymentRequests.forEach((request) =>
        addFulfilledRequest(request, 'incoming'),
    );

    return buckets;
};
